import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { pushSessionMessage } from '../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import { CrudType } from '../../../../utility/types/CrudType';
import { RaaRoles, sessionUserHasRolePrivileges } from '../../../../utility/SecurityRules';
import RaaThresholdRangeList from '../RaaThresholdRangeList/RaaThresholdRangeList';
import DeleteConfirmationModal from '../../../common/DeleteConfirmationModal';
import {
  deleteThresholdRange,
  fetchThresholds,
  selectAllThresholdRanges,
  selectThresholdCrudOperation,
  selectThresholdCrudStatus,
} from '../../../../redux/slices/thresholdRangeSlice/thresholdRangeSlice';
import SavedThresholdRangeViewModal from '../modals/SavedThresholdRangeViewModal';
import CannotDeleteInUseModal from '../../../common/CannotDeleteInUseModal';
import { BulkAlertingResourceType } from '../../../../utility/types/BulkAlertingResourceType';

const ThresholdRangeListViewConnected = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const thresholdRangeList = useSelector(selectAllThresholdRanges);
  const sessionInformation = useSelector(selectSessionInformation);
  const crudStatus = useSelector(selectThresholdCrudStatus);
  const crudType = useSelector(selectThresholdCrudOperation);

  const { activeSubscription, airSpeedScale } = sessionInformation;

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isCannotDeleteModalVisible, setIsCannotDeleteModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [thresholdRangeToDelete, setThresholdRangeToDelete] = useState({ id: null, name: null });
  const [thresholdRangeToView, setThresholdRangeToView] = useState({ id: null, name: null });

  const handleCreate = () => {
    history.push('/s/guidance/threshold-ranges/new');
  };

  const handleDelete = (thresholdRangeId) => {
    const targetThresholdRange = thresholdRangeList.find(
      (thresholdRange) => thresholdRange.id === thresholdRangeId,
    );
    if (targetThresholdRange && targetThresholdRange.alertCount > 0) {
      setIsCannotDeleteModalVisible(true);
    } else {
      setIsDeleteModalVisible(true);
    }
    setThresholdRangeToDelete({
      id: thresholdRangeId,
      name: targetThresholdRange.name,
    });
  };

  const handleView = (thresholdRangeId) => {
    const targetThresholdRange = thresholdRangeList.find(
      (thresholdRange) => thresholdRange.id === thresholdRangeId,
    );
    setIsViewModalVisible(true);
    setThresholdRangeToView({
      id: thresholdRangeId,
      name: targetThresholdRange.name,
    });
  };

  const closeCannotDeleteModal = () => {
    setIsCannotDeleteModalVisible(false);
    setThresholdRangeToDelete({ id: null, name: null });
  };

  const closeDeleteModal = () => {
    setIsDeleteModalVisible(false);
    setThresholdRangeToDelete({ id: null, name: null });
  };

  const handleConfirmDelete = () => {
    const { id } = thresholdRangeToDelete;
    dispatch(deleteThresholdRange(id));
    closeDeleteModal();
  };

  const handleDeleteSuccess = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Threshold Range Deleted',
      body: 'Threshold Range was successfully deleted.',
      durationInMillis: 10000,
    }));
    dispatch(fetchThresholds());
  };

  const handleDeleteFailure = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title: 'Error Deleting',
      body: 'Unexpected error occurred deleting Threshold Range.',
      durationInMillis: 10000,
    }));
  };

  const handleEdit = (thresholdRangeId) => {
    history.push(`/s/guidance/threshold-ranges/${thresholdRangeId}`);
  };

  const handleClone = (thresholdRangeId) => {
    history.push(`/s/guidance/threshold-ranges/new/${thresholdRangeId}`);
  };

  const handleASHRAERangeClick = () => {
    history.push('/s/guidance/established/ashrae-data-center');
  };

  const handleNWSRangeClick = () => {
    history.push('/s/guidance/established/nws-heat-index');
  };

  useEffect(() => {
    if (crudType === CrudType.DELETE) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleDeleteSuccess();
          break;
        case CrudStatus.ERROR:
          handleDeleteFailure();
          break;
        default:
          break;
      }
    }
  }, [crudType, crudStatus]);

  const hasEditPrivileges = sessionUserHasRolePrivileges(
    activeSubscription,
    [RaaRoles.ADMIN, RaaRoles.MANAGER],
  );

  return (
    <div>
      <div className="row mb-3">
        <div className="col-auto">
          <h2>Threshold Ranges</h2>
        </div>
        <div className="col-auto ml-auto">
          {hasEditPrivileges && (
            <button type="button" className="btn btn-primary" onClick={handleCreate}>
              New Threshold Range
            </button>
          )}
        </div>
      </div>
      <div className="row mb-2">
        <div className="col">
          <Icon icon="circle-question" />
          &nbsp;
          <a href="https://avtech.com/articles/23439/" target="_blank" rel="noopener noreferrer">
            About Threshold Ranges
          </a>
        </div>
      </div>
      <hr />
      <RaaThresholdRangeList
        hasEditPrivileges={hasEditPrivileges}
        onCreate={handleCreate}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onASHRAERangeClick={handleASHRAERangeClick}
        onNWSRangeClick={handleNWSRangeClick}
        temperatureScale={sessionInformation.temperatureScale}
        thresholdRangeList={thresholdRangeList}
        onClone={handleClone}
        onView={handleView}
        airSpeedScale={airSpeedScale}
      />
      {isDeleteModalVisible && (
        <DeleteConfirmationModal
          itemId={thresholdRangeToDelete.id}
          itemName={thresholdRangeToDelete.name}
          handleDelete={handleConfirmDelete}
          deleteButtonLabel="Delete Range"
        />
      )}
      {isViewModalVisible && (
      <SavedThresholdRangeViewModal
        thresholdRangeId={thresholdRangeToView.id}
        thresholdRangeName={thresholdRangeToView.name}
        handleClose={() => setIsViewModalVisible(false)}
      />
      )}
      {isCannotDeleteModalVisible && (
      <CannotDeleteInUseModal
        itemName={thresholdRangeToDelete.name}
        itemType={BulkAlertingResourceType.SHARED_THRESHOLD}
        handleClose={closeCannotDeleteModal}
      />
      )}
    </div>
  );
};

export default ThresholdRangeListViewConnected;
